import { TELEHEALTH_DEFAULTS, TIME_CONVERSION, CONSULTATION_STATUS } from '@shared/constants';
import type { AppError, AsyncResult } from '@shared/utils';
import { err, ok } from '@shared/utils';
import { getEnvironmentValue } from '@shared/utils';
import { ENVIRONMENT_VALUES } from '@shared/utils';
import { ConsultationRepository } from '@shared/db';
import type { TelehealthAdapter } from '../../src/interfaces/TelehealthAdapter';
import type { TelehealthMeetingComponentProps } from '../../src/interfaces/TelehealthMeetingComponentProps';
import type { TelehealthReservationInput } from '../../src/interfaces/TelehealthReservationInput';
import type { TelehealthReservationResult } from '../../src/interfaces/TelehealthReservationResult';
import type { TelehealthSessionCredentials } from '../../src/interfaces/TelehealthSessionCredentials';
import type { TelehealthSessionInput } from '../../src/interfaces/TelehealthSessionInput';
import { JITSI_CONSTANTS } from '../constants/JITSI_CONSTANTS';
import { mintJaasJwt } from '../functions/mintJaasJwt';
import { parseJitsiRoomName } from '../functions/parseJitsiRoomName';

const SERVER_ONLY_ERROR = {
  errorCode: 'TELEHEALTH_SERVER_ONLY' as const,
  errorMessage: 'getMeetingComponent is not available on server; use browser adapter',
};

/**
 * @group Adapters
 *
 * @summary
 * <blockquote>Server-side Jitsi/JaaS adapter. Mints JWT credentials, validates rooms
 * against consultations. getMeetingComponent returns err (browser-only).</blockquote>
 *
 * @example
 * ```typescript
 * import { JitsiServerTelehealthAdapter } from '@shared/telehealth/jitsi';
 *
 * const adapter = new JitsiServerTelehealthAdapter();
 * ```
 */
export class JitsiServerTelehealthAdapter implements TelehealthAdapter {
  readonly supportedRuntimes = ['server'] as const;
  readonly providerName = JITSI_CONSTANTS.PROVIDER_NAME;
  private readonly consultationRepository = new ConsultationRepository();

  async mintSessionCredentials(
    input: TelehealthSessionInput
  ): AsyncResult<TelehealthSessionCredentials, AppError> {
    const appIdResult = await getEnvironmentValue(ENVIRONMENT_VALUES.JAAS.JAAS_APP_ID);
    const apiKeyIdResult = await getEnvironmentValue(ENVIRONMENT_VALUES.JAAS.JAAS_API_KEY_ID);
    const privateKeyResult = await getEnvironmentValue(ENVIRONMENT_VALUES.JAAS.JAAS_PRIVATE_KEY);
    const domainResult = await getEnvironmentValue(ENVIRONMENT_VALUES.JAAS.JAAS_DOMAIN, true);

    if (appIdResult.isErr || apiKeyIdResult.isErr || privateKeyResult.isErr) {
      return err({
        errorCode: 'JAAS_CONFIG_MISSING',
        errorMessage: 'JaaS credentials not configured (APP_ID, API_KEY_ID, PRIVATE_KEY)',
      });
    }

    const domain = domainResult.isOk ? domainResult.value : JITSI_CONSTANTS.DEFAULT_DOMAIN;

    const jwtResult = await mintJaasJwt({
      appId: appIdResult.value,
      apiKeyId: apiKeyIdResult.value,
      privateKey: privateKeyResult.value,
      roomName: input.roomName,
      userId: input.userId,
      userName: input.userName,
      isModerator: input.isModerator,
      ttlSeconds: input.ttlSeconds,
    });

    if (jwtResult.isErr) {
      return err(jwtResult.error);
    }

    const serverUrl = `https://${domain}`;
    return ok({
      providerName: JITSI_CONSTANTS.PROVIDER_NAME,
      roomIdentifier: input.roomName,
      serverUrl,
      token: jwtResult.value,
    });
  }

  async validateRoom(
    input: TelehealthReservationInput
  ): AsyncResult<TelehealthReservationResult, AppError> {
    const roomName = parseJitsiRoomName(input.roomIdentifier);
    const consultationResult = await this.consultationRepository.getConsultationByExternalId(
      roomName
    );

    if (consultationResult.isErr) {
      return err(consultationResult.error);
    }

    const consultation = consultationResult.value;
    if (!consultation) {
      return err({
        errorCode: 'CONSULTATION_NOT_FOUND',
        errorMessage: `No consultation found for room: ${roomName}`,
      });
    }

    if (consultation.status !== CONSULTATION_STATUS.IN_PROGRESS) {
      return err({
        errorCode: 'CONSULTATION_INVALID_STATUS',
        errorMessage: `Consultation must be in_progress to join. Current status: ${consultation.status}`,
      });
    }

    const scheduledFor = consultation.scheduled_for ?? new Date();
    const durationMinutes = consultation.duration_minutes ?? TELEHEALTH_DEFAULTS.CONSULTATION_DURATION_MINUTES;

    return ok({
      duration: durationMinutes * TIME_CONVERSION.SECONDS_PER_MINUTE,
      name: consultation.external_id,
      startTime: scheduledFor.toISOString(),
    });
  }

  async getMeetingComponent(
    _props: TelehealthMeetingComponentProps
  ): AsyncResult<import('react').ComponentType<TelehealthMeetingComponentProps>, AppError> {
    return err(SERVER_ONLY_ERROR);
  }
}
